/**
 * Early-bird discount tiers — the ONE copy the price label the entrant reads
 * and the registration callable that records the discount both run.
 *
 * A tier is `{ id, daysBefore, discountPct }`: "register at least N days
 * before the start and pay P% less". It is stored relative to the start date
 * rather than as an absolute date so a template copy or a moved start date
 * keeps it correct. The same shape lives on a competition
 * (`settings.entryFee.earlyBird`) and on an event (`entryFee.earlyBird`).
 *
 * The client and the server must agree to the millisecond on which tier is
 * live, because the discounted amount decides `entryFeePaid`, and an unpaid
 * competitor drops out of the ranking. So the cutoff is read with the same
 * zone resolver as the registration window (`compDateMillis`), in the GYM's
 * timezone, never the browser's.
 */
import { compDateMillis, DEFAULT_COMP_TIMEZONE } from "./registrationWindow.js";

/** Most tiers a fee may carry; more is a UI the entrant cannot read at a glance. */
const TIER_LIMIT = 5;
/** Calendar-day arithmetic runs on UTC-midnight values, where every day is 24 h. */
const UTC_DAY_MS = 24 * 60 * 60 * 1000;

const clampPct = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
};

const isIntIn = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;

/**
 * The validated tier list: what every reader works from, so a hand-edited or
 * half-saved document cannot make the two sides disagree.
 *
 * A row with an out-of-range `daysBefore` (int 1–365) or `discountPct`
 * (int 1–100) is dropped, not repaired — a guessed discount is money.
 *
 * Ids are unique in the result, because the registration's snapshot refers to
 * its tier by id and an override picks a tier by id. An explicit id is kept
 * whenever it can be (the first row carrying it wins); a row without a usable
 * id, or repeating one already taken, gets a deterministic one —
 * `eb_d{daysBefore}`, then `eb_d{daysBefore}_2`, `_3`, … until it is free — so
 * the same document always yields the same ids. Explicit ids are reserved
 * first, so a generated id never steals one a snapshot may already name.
 * For a repeated `daysBefore` the first row wins. Sorted by `daysBefore`
 * descending (earliest cutoff first), at most five.
 *
 * @param {unknown} raw
 * @returns {Array<{ id: string, daysBefore: number, discountPct: number }>}
 */
export function normalizeEarlyBirdTiers(raw) {
  if (!Array.isArray(raw)) return [];
  const seenDays = new Set();
  const rows = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const { daysBefore, discountPct } = row;
    if (!isIntIn(daysBefore, 1, 365) || !isIntIn(discountPct, 1, 100)) continue;
    if (seenDays.has(daysBefore)) continue;
    seenDays.add(daysBefore);
    const explicitId = typeof row.id === "string" && row.id.trim() ? row.id.trim() : null;
    rows.push({ explicitId, daysBefore, discountPct });
  }
  const taken = new Set();
  const ids = rows.map((r) => {
    if (!r.explicitId || taken.has(r.explicitId)) return null;
    taken.add(r.explicitId);
    return r.explicitId;
  });
  const out = rows.map((r, i) => {
    let id = ids[i];
    if (id == null) {
      const base = `eb_d${r.daysBefore}`;
      id = base;
      for (let n = 2; taken.has(id); n += 1) id = `${base}_${n}`;
      taken.add(id);
    }
    return { id, daysBefore: r.daysBefore, discountPct: r.discountPct };
  });
  out.sort((a, b) => b.daysBefore - a.daysBefore);
  return out.slice(0, TIER_LIMIT);
}

/** "YYYY-MM-DD" of a UTC-midnight millis value. */
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);

/**
 * The last millisecond a tier is live: 23:59:59.999 on the calendar day
 * `startDate − daysBefore`, in the gym's timezone.
 *
 * Computed as "midnight of the following day, minus 1 ms" through
 * `compDateMillis`, so a cutoff on the night the clocks change is still the
 * end of that local day (the day is 23 or 25 hours long there, not 24).
 * `startDate` is the competition / event start ("YYYY-MM-DD", optionally with
 * a time — only its date part counts). Null when it is missing or not a real
 * calendar date: without a start date no tier can be live.
 *
 * @returns {number|null}
 */
export function earlyBirdCutoffMs(startDate, daysBefore, timezone) {
  if (typeof startDate !== "string" || !Number.isInteger(daysBefore)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(startDate);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const startUtc = Date.UTC(y, mo - 1, d);
  // Date.UTC rolls "02-31" over into March; a date that does not round-trip is not a date.
  if (Number.isNaN(startUtc) || isoDay(startUtc) !== m[0]) return null;
  const nextDay = isoDay(startUtc - daysBefore * UTC_DAY_MS + UTC_DAY_MS);
  const nextMidnight = compDateMillis(nextDay, timezone || DEFAULT_COMP_TIMEZONE);
  return nextMidnight == null ? null : nextMidnight - 1;
}

/**
 * The tier live at `atMs`, or null. When several are live the LARGEST
 * discount wins, independent of the order the organiser entered them in.
 * `atMs` is the caller's clock: the server's at registration (it decides),
 * the browser's only for display.
 */
export function activeEarlyBirdTier(tiers, { startDate, timezone, atMs } = {}) {
  if (!Number.isFinite(atMs)) return null;
  let best = null;
  for (const tier of normalizeEarlyBirdTiers(tiers)) {
    const cutoff = earlyBirdCutoffMs(startDate, tier.daysBefore, timezone);
    if (cutoff == null || atMs > cutoff) continue;
    if (!best || tier.discountPct > best.discountPct) best = tier;
  }
  return best;
}

/**
 * The price ladder in time order, for display: `[{ untilMs, discountPct,
 * amount }, …, { untilMs: null, discountPct: 0, amount: base }]`.
 *
 * Each step is valid up to its `untilMs` and carries the discount that is
 * largest-live at that time — the same rule as `activeEarlyBirdTier`. A step
 * whose discount is not larger than the next later step's would only repeat
 * it, so it is merged into that one. Already-expired steps are NOT dropped:
 * this function takes no "now", the caller filters what it shows.
 */
export function earlyBirdSchedule(tiers, { startDate, timezone, baseAmount } = {}) {
  const timed = [];
  for (const tier of normalizeEarlyBirdTiers(tiers)) {
    const untilMs = earlyBirdCutoffMs(startDate, tier.daysBefore, timezone);
    if (untilMs != null) timed.push({ untilMs, pct: tier.discountPct });
  }
  timed.sort((a, b) => a.untilMs - b.untilMs);
  // Live discount just before step i's cutoff = the largest among tiers ending at or after it.
  const liveFrom = new Array(timed.length);
  let runningMax = 0;
  for (let i = timed.length - 1; i >= 0; i -= 1) {
    runningMax = Math.max(runningMax, timed[i].pct);
    liveFrom[i] = runningMax;
  }
  const steps = [];
  for (let i = 0; i < timed.length; i += 1) {
    const next = i + 1 < timed.length ? liveFrom[i + 1] : 0;
    if (liveFrom[i] <= next) continue;
    steps.push({ untilMs: timed[i].untilMs, discountPct: liveFrom[i], amount: discountedAmount(baseAmount, liveFrom[i]) });
  }
  steps.push({ untilMs: null, discountPct: 0, amount: discountedAmount(baseAmount, 0) });
  return steps;
}

/**
 * `base` less `pct` percent, rounded to a whole currency unit. The ONE place
 * an early-bird price is rounded; a fee sum rounds per item through here and
 * adds the rounded items, so a payer's total equals the sum of the lines the
 * export prints. A non-numeric pct is no discount; it is clamped to 0–100.
 *
 * With no discount the base comes back UNROUNDED: whole-unit rounding is the
 * rule for a discounted price only. Rounding a 12.5 EUR fee to 13 would make
 * an entrant who paid the 12.5 the gym asked for read as unpaid, and an
 * unpaid competitor drops out of the ranking on the next recompute.
 */
export function discountedAmount(base, pct) {
  const amount = Number(base) || 0;
  const p = clampPct(pct);
  if (p <= 0) return amount;
  return Math.round((amount * (100 - p)) / 100);
}

/**
 * The early-bird snapshot an entry earns at `atMs`, or null (full price) —
 * what the registration callables stamp, and what an "auto" override
 * re-judges. One copy for competitions and events, so the two can never
 * disagree on when a fee has a tier.
 *
 * `enabled` is the fee's `enabled` flag and must be `true`: a fee that is
 * switched off has nothing to discount, so no snapshot — even when tiers are
 * configured. `atMs` is the server's clock (registration) or the entry's own
 * moment ("auto"); a non-finite one means "unknown", and an unknown moment
 * earns no tier.
 *
 * @param {unknown} tiers  the raw stored tier list
 * @param {{ startDate?: string, timezone?: string, atMs: number, enabled: boolean }} at
 * @returns {{ tierId: string, discountPct: number, source: "auto" } | null}
 */
export function earlyBirdSnapshotAt(tiers, { startDate, timezone, atMs, enabled } = {}) {
  if (enabled !== true) return null;
  const tier = activeEarlyBirdTier(tiers, { startDate, timezone, atMs });
  return tier ? { tierId: tier.id, discountPct: tier.discountPct, source: "auto" } : null;
}

/**
 * The snapshot an organiser's early-bird override resolves to:
 *   - "none" → `{ tierId: null, discountPct: 0, source: "staff" }` (full price
 *     recorded on purpose, so a later "auto" is a visible decision);
 *   - "auto" → `earlyBirdSnapshotAt` at the entry's own moment (`entryAtMs`),
 *     against the CURRENT tiers — it undoes a correction, it does not re-price
 *     an early entry at today's rate; null when no tier was live then or the
 *     fee is off;
 *   - a tier id of the CURRENT tiers → that tier at its percentage today,
 *     `source: "staff"` (a later edit of the tier does not reach it).
 *
 * Returns `{ snapshot }`, or `{ error: "unknown-tier" }` for an id that is
 * not a current tier (or not a string at all). The callables map the error to
 * their own HttpsError and keep their own caller / target checks — who may
 * override, and on which entries, differs between competitions and events.
 *
 * @param {unknown} tiers  the raw stored tier list
 * @param {unknown} value  tier id | "none" | "auto"
 * @param {{ startDate?: string, timezone?: string, entryAtMs: number, enabled: boolean }} ctx
 * @returns {{ snapshot: object|null } | { error: "unknown-tier" }}
 */
export function resolveEarlyBirdOverride(tiers, value, { startDate, timezone, entryAtMs, enabled } = {}) {
  if (value === "none") return { snapshot: { tierId: null, discountPct: 0, source: "staff" } };
  if (value === "auto") {
    return { snapshot: earlyBirdSnapshotAt(tiers, { startDate, timezone, atMs: entryAtMs, enabled }) };
  }
  const tier = typeof value === "string"
    ? normalizeEarlyBirdTiers(tiers).find((t) => t.id === value)
    : null;
  if (!tier) return { error: "unknown-tier" };
  return { snapshot: { tierId: tier.id, discountPct: tier.discountPct, source: "staff" } };
}

/**
 * The discount percentage a registration's `earlyBird` snapshot carries, 0
 * when there is none. Every fee computation reads the snapshot through this,
 * so a missing, null or malformed snapshot is always "full price".
 */
export function earlyBirdPct(snapshot) {
  return snapshot && typeof snapshot === "object" ? clampPct(snapshot.discountPct) : 0;
}
